/**
 * Security Settings Component
 * Manage password and email verification settings
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../services/supabase";
import Button from "../common/Button";
import Input from "../common/Input";
import Card from "../ui/Card";
import Modal from "../ui/Modal";

const SecuritySettings: React.FC = () => {
  const { user: _user } = useAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [_emailVerified, setEmailVerified] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkEmailVerification = async () => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      setEmailVerified(!!authUser?.email_confirmed_at);
    };
    checkEmailVerification();
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirm password do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });
      if (error) {
        throw error;
      }

      setPasswordSuccess("Password changed successfully");
      setTimeout(() => {
        setIsPasswordModalOpen(false);
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordSuccess("");
      }, 2000);
    } catch (error: any) {
      setPasswordError(error.message || "Failed to change password");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Security Settings
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage your password and authentication preferences
        </p>
      </div>

      <Card padding="lg">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900">Password</h3>
              <p className="mt-1 text-sm text-gray-600">
                Change your password to keep your account secure
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => setIsPasswordModalOpen(true)}
            >
              Change Password
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setPasswordForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setPasswordError("");
          setPasswordSuccess("");
        }}
        title="Change Password"
        size="md"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {passwordError && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">{passwordError}</p>
            </div>
          )}
          {passwordSuccess && (
            <div className="p-3 rounded-lg bg-green-50 border border-green-200">
              <p className="text-sm text-green-800">{passwordSuccess}</p>
            </div>
          )}

          <Input
            name="currentPassword"
            type="password"
            label="Current Password"
            value={passwordForm.currentPassword}
            onChange={handlePasswordChange}
            placeholder="Enter current password"
            required
            fullWidth
          />
          <Input
            name="newPassword"
            type="password"
            label="New Password"
            value={passwordForm.newPassword}
            onChange={handlePasswordChange}
            placeholder="Enter new password (min 8 characters)"
            required
            fullWidth
          />
          <Input
            name="confirmPassword"
            type="password"
            label="Confirm New Password"
            value={passwordForm.confirmPassword}
            onChange={handlePasswordChange}
            placeholder="Re-enter new password"
            required
            fullWidth
          />

          <div className="flex justify-end space-x-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsPasswordModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Changing Password..." : "Change Password"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SecuritySettings;
